import { useRef, useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, ScanLine, Sparkles, RefreshCw, PackagePlus, Zap, Split } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CourierSelect from "@/components/CourierSelect";
import RecipientPicker from "@/components/RecipientPicker";
import ClientVendorPicker from "@/components/ClientVendorPicker";
import ParcelTypeSelect from "@/components/ParcelTypeSelect";
import BulkUploadButton from "@/components/BulkUploadButton";
import ParcelConditionSection, { DEFAULT_CONDITION } from "@/components/ParcelConditionSection";
import ItemBreakdownEditor from "@/components/ItemBreakdownEditor";
import ShipmentTypeRadio from "@/components/ShipmentTypeRadio";
import WeightBlock from "@/components/WeightBlock";
import InwardCategoryRadio from "@/components/InwardCategoryRadio";
import BookParcelDialog from "@/components/BookParcelDialog";

const emptyForm = {
  awb_number: "", courier: "", sender_name: "", sender_address: "", sender_phone: "",
  entry_mode: "Courier", scope: "Domestic", shipment_type: "Official",
  route_id: "",   // Phase 1 · Feb 2026 · Route Master link (only Active routes offered)
  recipient_id: null, recipient_name: "", recipient_address: "", recipient_phone: "", recipient_department: "",
  actual_weight_kg: 0.5, length_cm: "", breadth_cm: "", height_cm: "",
  weight_kg: 0.5, dimensions: "", parcel_type: "Document", item_count: 1, notes: "", photo_url: "",
  priority: false,
  is_rto: false,
  rto_reason: "",
  on_hold: false,
  hold_reason: "",
  condition: DEFAULT_CONDITION,
  items: [],
  allow_split_delivery: false,
};

// Recipient status → UI copy for the Inward Log validation banner
const RECIPIENT_BANNER = {
  active:    { tone: "ok",    text: "Employee is active. You can proceed." },
  inactive:  { tone: "warn",  text: "Employee is marked INACTIVE. Choose Return-to-Office (RTO) or Hold." },
  left:      { tone: "warn",  text: "Employee has LEFT the organisation. Choose Return-to-Office (RTO) or Hold." },
  not_found: { tone: "block", text: "This name is not in the User Directory. Add the employee or fix the name before saving." },
};

// RTO reason presets — kept short so manifests stay consistent across operators.
const RTO_REASON_PRESETS = [
  "Employee left",
  "Wrong address",
  "Refused",
  "Damaged",
];

export default function InwardEntryPage() {
  const [form, setForm] = useState(emptyForm);
  const [imgUrl, setImgUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bookImmediately, setBookImmediately] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [savedParcel, setSavedParcel] = useState(null);
  const fileRef = useRef(null);
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const logId = searchParams.get("log_id") || null;
  const [rtoLocked, setRtoLocked] = useState(false);
  const [recipientCheck, setRecipientCheck] = useState(null); // {status,match} | null
  const [routes, setRoutes] = useState([]);                    // Phase 1 · active routes for dropdown

  // Load only active routes for this company (tenant-scoped by backend).
  useEffect(() => {
    api.get("/routes", { params: { status: "active" } })
       .then(r => setRoutes(r.data || []))
       .catch(() => setRoutes([]));
  }, []);

  // Debounced recipient validation — hits /recipients/validate whenever the picker
  // resolves an id, or the manual name changes. Status controls the banner + gating.
  useEffect(() => {
    const id = form.recipient_id;
    const name = (form.recipient_name || "").trim();
    if (!id && !name) { setRecipientCheck(null); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/recipients/validate", {
          params: id ? { id } : { name },
        });
        setRecipientCheck(data);
      } catch { setRecipientCheck(null); }
    }, 300);
    return () => clearTimeout(timer);
  }, [form.recipient_id, form.recipient_name]);

  const recipientBlocked =
    recipientCheck?.status === "not_found" ||
    ((recipientCheck?.status === "inactive" || recipientCheck?.status === "left") &&
      !form.is_rto && !form.on_hold);

  // Pre-fill from Scan handoff (mobile flow)
  useEffect(() => {
    const x = location.state?.extracted;
    if (x) {
      const m = x.matched_recipient;
      setForm((s) => ({
        ...s,
        awb_number: x.awb_number || x.tracking_number || s.awb_number,
        courier: x.courier || s.courier,
        sender_name: x.sender_name || s.sender_name,
        sender_address: x.sender_address || s.sender_address,
        sender_phone: x.sender_phone || s.sender_phone,
        // Prefer directory match if the AI found one — otherwise use raw OCR text
        recipient_id: m?.id || s.recipient_id,
        recipient_name: m?.name || x.recipient_name || s.recipient_name,
        recipient_department: m?.department || x.recipient_department || s.recipient_department,
        recipient_phone: m?.phone || x.recipient_phone || s.recipient_phone,
        recipient_address: x.recipient_address || s.recipient_address,
        weight_kg: x.weight_kg || s.weight_kg,
        dimensions: x.dimensions || s.dimensions,
        parcel_type: x.parcel_type || s.parcel_type,
        photo_url: x.photo_url || "",
      }));
      if (x.photo_url) setImgUrl(fileUrl(x.photo_url));
      if (m) toast.success(`Auto-matched ${m.name}`, { description: m.department || "Employee directory" });
    }
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setImgUrl(preview);
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/scan-label", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const x = data.extracted || {};
      const m = x.matched_recipient;
      setForm((s) => ({
        ...s,
        awb_number: x.awb_number || x.tracking_number || s.awb_number,
        courier: x.courier || s.courier,
        sender_name: x.sender_name || s.sender_name,
        sender_address: x.sender_address || s.sender_address,
        sender_phone: x.sender_phone || s.sender_phone,
        // Prefer directory match if AI found one
        recipient_id: m?.id || s.recipient_id,
        recipient_name: m?.name || x.recipient_name || s.recipient_name,
        recipient_department: m?.department || s.recipient_department,
        recipient_phone: m?.phone || x.recipient_phone || s.recipient_phone,
        recipient_address: x.recipient_address || s.recipient_address,
        weight_kg: x.weight_kg || s.weight_kg,
        dimensions: x.dimensions || s.dimensions,
        parcel_type: x.parcel_type || s.parcel_type,
        photo_url: x.photo_url || "",
      }));
      if (x.photo_url) setImgUrl(fileUrl(x.photo_url));
      const suffix = m ? ` → matched ${m.name}` : "";
      toast.success("Label extracted" + (x.handwritten ? " (handwritten)" : ""),
        { description: `${x.courier || "?"} · AWB ${x.awb_number || x.tracking_number || "—"}${suffix}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI scan failed");
    } finally {
      setScanning(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.courier || !form.courier.trim()) return toast.error("Please select a Courier.");
    if (!form.sender_name || !form.recipient_name) return toast.error("Sender and recipient required");
    // Recipient Validation gate (Feb 2026 · client meeting fix):
    //  - not_found          → block silent accept
    //  - inactive/left      → require the user to pick RTO or Hold before saving
    if (recipientCheck?.status === "not_found") {
      return toast.error("Recipient not found in User Directory. Add them or correct the name before saving.");
    }
    if ((recipientCheck?.status === "inactive" || recipientCheck?.status === "left") &&
        !form.is_rto && !form.on_hold) {
      return toast.error("This employee is inactive. Choose RTO or Hold before saving.");
    }
    if (form.is_rto && !(form.rto_reason || "").trim()) {
      return toast.error("Please capture an RTO reason.");
    }
    setSaving(true);
    try {
      // Coerce empty-string numerics to null so Pydantic doesn't reject them
      const numOrNull = (v) => (v === "" || v === undefined || v === null ? null : Number(v));
      const payload = {
        ...form,
        log_id: logId,
        length_cm: numOrNull(form.length_cm),
        breadth_cm: numOrNull(form.breadth_cm),
        height_cm: numOrNull(form.height_cm),
        actual_weight_kg: numOrNull(form.actual_weight_kg),
        weight_kg: numOrNull(form.weight_kg) ?? 0.5,
      };
      const { data } = await api.post("/parcels/inward", payload);
      toast.success(`Inward saved · Reference ${data.tracking_number}`, {
        description: data.awb_number ? `AWB ${data.awb_number}` : undefined,
      });
      if (form.is_rto) setRtoLocked(true);
      if (bookImmediately) {
        // Keep user on this page — pop the Booking dialog with the freshly created parcel.
        setSavedParcel(data);
        setBookOpen(true);
      } else {
        nav("/app/parcels?direction=inward");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><PackagePlus size={12}/> New inward parcel</div>
          <h1 className="text-4xl font-display mt-1">Inward Entry.</h1>
          <p className="text-sm text-muted-foreground mt-1">Scan the label or key data in manually. AI OCR auto-fills fields.</p>
        </div>
        <BulkUploadButton direction="inward" testid="inward-bulk-btn" />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Scan card */}
        <div className="lg:col-span-2 border border-border bg-card p-6 h-fit">
          <div className="overline flex items-center gap-2"><Sparkles size={12} className="text-accent"/> AI Label Scan</div>
          <div className="mt-4 aspect-[4/5] bg-muted/40 scan-frame flex items-center justify-center relative border border-dashed border-border" data-testid="scan-preview">
            {imgUrl ? (
              <img src={imgUrl} alt="label" className="w-full h-full object-contain" />
            ) : (
              <div className="text-center px-6">
                <ScanLine size={40} className="mx-auto text-muted-foreground" />
                <div className="mt-3 text-sm">Drop or upload a shipping label.<br /> Gemini vision extracts fields.</div>
              </div>
            )}
            {scanning && <div className="scan-beam" />}
            {scanning && (
              <div className="absolute inset-0 bg-foreground/10 flex items-end justify-center pb-4">
                <div className="mono text-xs bg-foreground text-background px-3 py-1 uppercase tracking-widest">Scanning…</div>
              </div>
            )}
          </div>
          <input
            ref={fileRef} type="file" accept="image/*" capture="environment" hidden data-testid="scan-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button data-testid="scan-upload-btn" onClick={() => fileRef.current?.click()} disabled={scanning}
              className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              <Upload size={14} className="mr-2" /> Upload
            </Button>
            <Button data-testid="scan-reset-btn" variant="outline" onClick={() => { setImgUrl(null); setForm(emptyForm); }} className="rounded-md">
              <RefreshCw size={14} className="mr-2" /> Reset
            </Button>
          </div>
          <div className="mt-4 text-[11px] text-muted-foreground">Supported: JPG, PNG, WEBP. Max 1600×1600.</div>
        </div>

        {/* Form card */}
        <form onSubmit={submit} className="lg:col-span-3 border border-border bg-card p-6 space-y-6" data-testid="inward-form">
          {/* Reference ID + AWB row — both explicit and prominent */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="overline flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
                Internal Reference ID
                <span className="ml-1 text-[9px] font-bold text-primary bg-red-50 border border-primary px-1.5 py-0.5 rounded">AUTO</span>
              </span>
              <input readOnly value="MR-{TENANT_CODE}-{YYMMDD}-#### (assigned on save)"
                data-testid="in-reference-preview"
                className="mt-1 w-full h-10 px-3 rounded-md border border-dashed border-primary/40 bg-red-50/40 text-xs font-mono text-primary/80 cursor-not-allowed" />
              <div className="text-[10px] text-muted-foreground mt-1">Generated automatically on submit for chain-of-custody.</div>
            </div>
            <div>
              <span className="overline">AWB / Consignment #</span>
              <input value={form.awb_number} onChange={(e) => set("awb_number", e.target.value)}
                data-testid="in-awb"
                placeholder="Courier's tracking / consignment number"
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm font-mono focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
              <div className="text-[10px] text-muted-foreground mt-1">Optional. External courier's AWB — links this parcel to their tracking portal.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className="overline">Mode</span>
              <select data-testid="in-entry-mode" value={form.entry_mode}
                onChange={(e) => set("entry_mode", e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
                <option value="Courier">Courier</option>
                <option value="Postal">Postal</option>
                <option value="Intercompany">Intercompany</option>
              </select>
            </div>
            <div>
              <span className="overline">Scope</span>
              <select data-testid="in-scope" value={form.scope}
                onChange={(e) => set("scope", e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
                <option value="Domestic">Domestic</option>
                <option value="International">International</option>
              </select>
            </div>
            <div>
              <span className="overline">Courier</span>
              <CourierSelect value={form.courier} onChange={(v) => set("courier", v)} testid="in-courier" />
            </div>
          </div>
          <div className="border-t border-border pt-6 space-y-4">
            <div className="overline">Consignor (Sender · external)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ClientVendorPicker
                label="Consignor name"
                required
                kind="sender"
                testid="in-sender-name"
                value={form.sender_name ? { id: null, name: form.sender_name, phone: form.sender_phone, address: form.sender_address } : null}
                onChange={(r) => setForm((s) => ({
                  ...s,
                  sender_name: r.name || "",
                  sender_phone: r.phone || s.sender_phone,
                  sender_address: r.address || s.sender_address,
                }))}
                placeholder="Type or pick from Client/Vendor Master…"
              />
              <Field label="Consignor phone" value={form.sender_phone} onChange={(v) => set("sender_phone", v)} testid="in-sender-phone" />
            </div>
            <Field label="Consignor address" value={form.sender_address} onChange={(v) => set("sender_address", v)} textarea testid="in-sender-address" />
          </div>
          <div className="border-t border-border pt-6 space-y-4">
            <div className="overline">Consignee (Recipient · internal employee)</div>
            <RecipientPicker
              value={form.recipient_id ? { id: form.recipient_id, name: form.recipient_name, department: form.recipient_department } : null}
              onChange={(r) => setForm((s) => ({
                ...s, recipient_id: r.id, recipient_name: r.name,
                recipient_department: r.department || s.recipient_department,
                recipient_phone: r.phone || s.recipient_phone,
              }))}
              testid="in-recipient-picker"
              label="Pick employee from directory"
              allBranches={true}
            />
            {/* Recipient Validation Banner (Feb 2026) —
                Active → green info · Inactive/Left → amber warning with RTO/Hold CTAs · Not-found → red block */}
            {recipientCheck && (
              <div data-testid={`recipient-status-${recipientCheck.status}`}
                   className={`rounded-md border p-3 text-xs flex flex-wrap items-start gap-3
                     ${recipientCheck.status === "active" ? "border-emerald-300 bg-emerald-50 text-emerald-900" : ""}
                     ${recipientCheck.status === "inactive" || recipientCheck.status === "left" ? "border-amber-300 bg-amber-50 text-amber-900" : ""}
                     ${recipientCheck.status === "not_found" ? "border-red-400 bg-red-50 text-red-900" : ""}`}>
                <div className="flex-1 min-w-[280px]">
                  <div className="font-bold uppercase tracking-widest text-[10px] mb-0.5">
                    {recipientCheck.status === "active" && "✓ Recipient active"}
                    {recipientCheck.status === "inactive" && "⚠ Recipient inactive"}
                    {recipientCheck.status === "left"     && "⚠ Recipient has left"}
                    {recipientCheck.status === "not_found" && "✕ Recipient not found"}
                  </div>
                  <div>
                    {recipientCheck.status === "active" && "Employee is active. You can proceed."}
                    {recipientCheck.status === "inactive" && "This employee is marked INACTIVE. Pick Return-to-Office or Hold below to save the parcel."}
                    {recipientCheck.status === "left" && "This employee has LEFT the organisation. Pick Return-to-Office or Hold below to save the parcel."}
                    {recipientCheck.status === "not_found" && "No matching employee found in the User Directory. Add them via the picker or correct the name before saving. Silent acceptance is blocked."}
                  </div>
                </div>
                {(recipientCheck.status === "inactive" || recipientCheck.status === "left") && (
                  <div className="flex gap-2 shrink-0">
                    <button type="button" data-testid="recipient-action-rto"
                            onClick={() => setForm(s => ({ ...s, is_rto: true, on_hold: false }))}
                            className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border transition ${form.is_rto ? "bg-primary text-primary-foreground border-primary" : "border-primary/60 text-primary hover:bg-primary hover:text-primary-foreground"}`}>
                      Mark RTO
                    </button>
                    <button type="button" data-testid="recipient-action-hold"
                            onClick={() => setForm(s => ({ ...s, on_hold: true, is_rto: false }))}
                            className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border transition ${form.on_hold ? "bg-amber-600 text-white border-amber-600" : "border-amber-500/70 text-amber-800 hover:bg-amber-500 hover:text-white"}`}>
                      Hold at Mailroom
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Consignee name (override)" value={form.recipient_name} onChange={(v) => set("recipient_name", v)} testid="in-recipient-name" required />
              <Field label="Department" value={form.recipient_department} onChange={(v) => set("recipient_department", v)} testid="in-recipient-dept" />
              <Field label="Consignee phone" value={form.recipient_phone} onChange={(v) => set("recipient_phone", v)} testid="in-recipient-phone" />
            </div>
            <Field label="Delivery address / Desk" value={form.recipient_address} onChange={(v) => set("recipient_address", v)} textarea testid="in-recipient-address" />
            <ShipmentTypeRadio value={form.shipment_type} onChange={(v)=>set("shipment_type",v)} prefix="in" />
            <InwardCategoryRadio
              value={form.inward_category}
              onChange={(v)=>set("inward_category", v)}
              regulatorId={form.regulator_id}
              onRegulatorChange={(id)=>set("regulator_id", id)}
              onPriorityAutoToggle={(v)=>set("priority", v)}
            />
            {/* Phase 1 · Delivery Route (only Active routes shown) */}
            <label className="block">
              <span className="overline">Delivery Route <span className="text-muted-foreground font-normal">· optional</span></span>
              <select data-testid="in-route" value={form.route_id || ""}
                      onChange={(e) => set("route_id", e.target.value)}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
                <option value="">— no route —</option>
                {routes.map(r => (
                  <option key={r.id} value={r.id}>{r.name} · {r.code}</option>
                ))}
              </select>
              {routes.length === 0 && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  No active routes yet. Company admins can add them under <b>Master → Routes</b>.
                </div>
              )}
            </label>
          </div>
          <div className="border-t border-border pt-6 space-y-4">
            <div className="overline">Parcel</div>
            <WeightBlock form={form} set={set} prefix="in" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="# Items" mono type="number" step="1" value={form.item_count} onChange={(v) => set("item_count", Math.max(1, parseInt(v) || 1))} testid="in-item-count" />
              <div>
                <span className="overline">Type</span>
                <ParcelTypeSelect value={form.parcel_type} onChange={(v) => set("parcel_type", v)} testid="in-type" />
              </div>
              <Field label="Notes" value={form.notes} onChange={(v) => set("notes", v)} testid="in-notes" />
            </div>
            <ItemBreakdownEditor
              value={form.items}
              onChange={(items) => {
                const totalCount = items && items.length ? items.reduce((n,r) => n + (Number(r.count)||0), 0) : null;
                const totalWeight = items && items.length ? items.reduce((n,r) => n + ((Number(r.weight_kg)||0) * (Number(r.count)||0)), 0) : 0;
                setForm(s => ({
                  ...s,
                  items,
                  item_count: totalCount || s.item_count,
                  weight_kg: totalWeight > 0 ? Math.round(totalWeight * 1000) / 1000 : s.weight_kg,
                }));
              }}
              prefix="in-items"
            />
          </div>
          <div className="border-t border-border pt-6 space-y-4">
            <ParcelConditionSection
              value={form.condition}
              onChange={(c) => set("condition", c)}
              prefix="in-cond"
            />
            <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${form.allow_split_delivery ? "border-primary bg-red-50/60" : "border-border hover:border-primary/40"}`} data-testid="in-split-toggle-wrap">
              <input
                type="checkbox"
                data-testid="in-split-toggle"
                checked={!!form.allow_split_delivery}
                onChange={(e) => set("allow_split_delivery", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#BE1E1E] cursor-pointer"
                disabled={!form.items || form.items.length === 0}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <Split size={14} className={form.allow_split_delivery ? "text-primary" : "text-muted-foreground"} />
                  Allow split delivery
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Optional</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {form.items && form.items.length
                    ? "Recipient can pick up items in multiple visits — each split gets its own signature + timeline entry. Great for mixed batches (e.g. take documents now, fragile box later)."
                    : "Add an item breakdown above to enable split delivery."}
                </div>
              </div>
            </label>
            <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${form.priority ? "border-primary bg-red-50" : "border-border hover:border-primary/40"}`} data-testid="in-priority-toggle-wrap">
              <input
                type="checkbox"
                data-testid="in-priority-toggle"
                checked={!!form.priority}
                onChange={(e) => set("priority", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#BE1E1E] cursor-pointer"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <Zap size={14} className={form.priority ? "text-primary" : "text-muted-foreground"} />
                  Mark as Priority
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Optional</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Auto-emails company admin(s) and the recipient every 4 hours until this parcel is delivered, forwarded or returned.
                </div>
              </div>
            </label>
            {/* T3 · Return-To-Office */}
            <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${form.is_rto ? "border-primary bg-red-50/60" : "border-border hover:border-primary/40"} ${rtoLocked ? "opacity-60 cursor-not-allowed" : ""}`} data-testid="in-rto-toggle-wrap">
              <input
                type="checkbox"
                data-testid="in-rto-toggle"
                checked={!!form.is_rto}
                disabled={rtoLocked}
                onChange={(e) => set("is_rto", e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#BE1E1E] cursor-pointer disabled:cursor-not-allowed"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  Return To Office (RTO)
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-normal">Optional</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Tick when this parcel is a return coming back to the office. {rtoLocked ? "Already processed — locked to prevent duplicate RTO." : "Once saved, RTO cannot be toggled again on this record."}
                </div>
                {form.is_rto && (
                  <div className="mt-2 space-y-2" data-testid="in-rto-reason-block">
                    <div>
                      <span className="overline">RTO reason *</span>
                      <select
                        data-testid="in-rto-reason"
                        value={RTO_REASON_PRESETS.includes(form.rto_reason) ? form.rto_reason : (form.rto_reason ? "Other" : "")}
                        onChange={(e) => {
                          const v = e.target.value;
                          set("rto_reason", v === "Other" ? "" : v);
                        }}
                        required
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none">
                        <option value="">Select a reason…</option>
                        {RTO_REASON_PRESETS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                        <option value="Other">Other (type below)</option>
                      </select>
                    </div>
                    {(!RTO_REASON_PRESETS.includes(form.rto_reason)) && (
                      <div>
                        <span className="overline text-[10px] text-muted-foreground">Custom reason {form.rto_reason ? "" : "*"}</span>
                        <Input
                          data-testid="in-rto-reason-other"
                          value={form.rto_reason || ""}
                          onChange={(e) => set("rto_reason", e.target.value)}
                          placeholder="Free-text reason for the manifest"
                          className="mt-1 rounded-md text-sm"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </label>
            {form.on_hold && (
              <label className="flex items-start gap-3 border border-amber-300 bg-amber-50 rounded-md p-3" data-testid="in-hold-block">
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-semibold text-sm text-amber-900">
                    Hold at Mailroom
                    <button type="button" data-testid="in-hold-clear"
                            onClick={() => setForm(s => ({ ...s, on_hold: false, hold_reason: "" }))}
                            className="ml-auto text-[10px] uppercase tracking-widest text-amber-900 hover:underline">Clear</button>
                  </div>
                  <div className="text-xs text-amber-900/80 mt-0.5">
                    Parcel will be received but marked "on hold" instead of routing to the recipient. Use for inactive employees pending manager decision.
                  </div>
                  <div className="mt-2">
                    <span className="overline text-amber-900">Hold reason *</span>
                    <Input
                      data-testid="in-hold-reason"
                      value={form.hold_reason || ""}
                      onChange={(e) => set("hold_reason", e.target.value)}
                      placeholder="e.g. Employee inactive · Awaiting manager confirmation"
                      className="mt-1 rounded-md text-sm bg-white"
                      required
                    />
                  </div>
                </div>
              </label>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2 items-center flex-wrap">
            <label className="flex items-center gap-2 text-sm mr-auto cursor-pointer" data-testid="in-book-immediately-wrap">
              <input
                type="checkbox"
                data-testid="in-book-immediately"
                checked={bookImmediately}
                onChange={(e) => setBookImmediately(e.target.checked)}
                className="h-4 w-4 accent-[#BE1E1E]"
              />
              <span className="font-semibold">Book Parcel Immediately</span>
              <span className="text-[11px] text-muted-foreground">Opens the Booking window right after save</span>
            </label>
            <Button type="button" variant="outline" className="rounded-md" onClick={() => setForm(emptyForm)}>Clear</Button>
            <Button data-testid="in-submit" type="submit" disabled={saving || recipientBlocked}
              className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
              {saving ? "Saving…" : "Save Inward Parcel →"}
            </Button>
          </div>
        </form>
      </div>

      {/* Book-Parcel dialog: opens automatically when "Book Parcel Immediately" was ticked at save. */}
      <BookParcelDialog
        open={bookOpen}
        selectedParcels={savedParcel ? [savedParcel] : []}
        onClose={() => {
          // Cancelled without booking — parcel remains saved with booking_status=not_booked.
          setBookOpen(false);
          setSavedParcel(null);
          setBookImmediately(false);
          setForm(emptyForm);
          nav("/app/parcels?direction=inward");
        }}
        onBooked={() => {
          setBookOpen(false);
          setSavedParcel(null);
          setBookImmediately(false);
          setForm(emptyForm);
          nav("/app/parcels?direction=inward");
        }}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", mono, required, placeholder, textarea, testid, step }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      {textarea ? (
        <Textarea data-testid={testid} value={value || ""} required={required} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)} rows={2}
          className={`mt-1 rounded-md border-input focus-visible:ring-primary ${mono ? "font-mono" : ""}`} />
      ) : (
        <Input data-testid={testid} type={type} step={step} value={value ?? ""} required={required} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`mt-1 rounded-md border-input focus-visible:ring-primary ${mono ? "font-mono" : ""}`} />
      )}
    </label>
  );
}
